import { useState } from "react";
import { Plus, GripVertical, Trash2, ChevronDown, ChevronUp } from "lucide-react";

export interface ItineraryDay {
  day: number;
  title: string;
  titleSk?: string;
  description: string;
  descriptionSk?: string;
  meals?: string[];
  accommodation?: string;
  highlights?: string[];
}

interface Props {
  value: ItineraryDay[];
  onChange: (value: ItineraryDay[]) => void;
}

export default function ItineraryEditor({ value, onChange }: Props) {
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const addDay = () => {
    const newDay: ItineraryDay = {
      day: value.length + 1,
      title: "",
      description: "",
      meals: [],
      highlights: [],
    };
    onChange([...value, newDay]);
    setExpandedDay(value.length);
  };

  const updateDay = (index: number, updates: Partial<ItineraryDay>) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], ...updates };
    onChange(newValue);
  };

  const removeDay = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    // Renumber days
    newValue.forEach((day, i) => {
      day.day = i + 1;
    });
    onChange(newValue);
  };

  const moveDay = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= value.length) return;

    const newValue = [...value];
    const [moved] = newValue.splice(fromIndex, 1);
    newValue.splice(toIndex, 0, moved);
    // Renumber days
    newValue.forEach((day, i) => {
      day.day = i + 1;
    });
    onChange(newValue);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveDay(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Itinerář ({value.length} dní)
        </h3>
        <button
          type="button"
          onClick={addDay}
          className="btn-ghost btn-sm text-primary-600"
        >
          <Plus className="h-4 w-4" />
          Přidat den
        </button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
          <p className="text-gray-500">Zatím žádné dny v itineráři</p>
          <button
            type="button"
            onClick={addDay}
            className="btn-primary btn-sm mt-4"
          >
            <Plus className="h-4 w-4" />
            Přidat první den
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((day, index) => (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`rounded-lg border bg-white dark:bg-gray-800 ${
                draggedIndex === index
                  ? "border-primary-500 opacity-50"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              {/* Day header */}
              <div
                className="flex cursor-pointer items-center gap-3 p-4"
                onClick={() => setExpandedDay(expandedDay === index ? null : index)}
              >
                <div className="cursor-grab text-gray-400 hover:text-gray-600">
                  <GripVertical className="h-5 w-5" />
                </div>

                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                  {day.day}
                </div>

                <div className="flex-1">
                  <input
                    type="text"
                    value={day.title}
                    onChange={(e) => updateDay(index, { title: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`Den ${day.day}: Název`}
                    className="w-full border-none bg-transparent p-0 font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveDay(index, index - 1);
                    }}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveDay(index, index + 1);
                    }}
                    disabled={index === value.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDay(index);
                    }}
                    className="p-1 text-error-500 hover:text-error-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  {expandedDay === index ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Day content (expanded) */}
              {expandedDay === index && (
                <div className="space-y-4 border-t border-gray-200 p-4 dark:border-gray-700">
                  {/* Title SK */}
                  <div>
                    <label className="label">Název (SK)</label>
                    <input
                      type="text"
                      value={day.titleSk || ""}
                      onChange={(e) => updateDay(index, { titleSk: e.target.value })}
                      placeholder="Slovenský název"
                      className="input"
                    />
                  </div>

                  {/* Description CZ */}
                  <div>
                    <label className="label">Popis (CZ)</label>
                    <textarea
                      value={day.description}
                      onChange={(e) => updateDay(index, { description: e.target.value })}
                      rows={3}
                      placeholder="Podrobný popis dne..."
                      className="input"
                    />
                  </div>

                  {/* Description SK */}
                  <div>
                    <label className="label">Popis (SK)</label>
                    <textarea
                      value={day.descriptionSk || ""}
                      onChange={(e) => updateDay(index, { descriptionSk: e.target.value })}
                      rows={3}
                      placeholder="Slovenský popis..."
                      className="input"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Accommodation */}
                    <div>
                      <label className="label">Ubytování</label>
                      <input
                        type="text"
                        value={day.accommodation || ""}
                        onChange={(e) => updateDay(index, { accommodation: e.target.value })}
                        placeholder="např. Hotel 4* v centru"
                        className="input"
                      />
                    </div>

                    {/* Meals */}
                    <div>
                      <label className="label">Strava</label>
                      <div className="flex flex-wrap gap-2">
                        {["snídaně", "oběd", "večeře"].map((meal) => (
                          <label key={meal} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={day.meals?.includes(meal) || false}
                              onChange={(e) => {
                                const meals = day.meals || [];
                                if (e.target.checked) {
                                  updateDay(index, { meals: [...meals, meal] });
                                } else {
                                  updateDay(index, { meals: meals.filter((m) => m !== meal) });
                                }
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {meal.charAt(0).toUpperCase() + meal.slice(1)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div>
                    <label className="label">Highlights (oddělte čárkou)</label>
                    <input
                      type="text"
                      value={day.highlights?.join(", ") || ""}
                      onChange={(e) => {
                        const highlights = e.target.value
                          .split(",")
                          .map((h) => h.trim())
                          .filter(Boolean);
                        updateDay(index, { highlights });
                      }}
                      placeholder="např. Návštěva chrámu, Tradiční oběd"
                      className="input"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
